import api from './client';

export interface LoginData { email: string; password: string; remember_me?: boolean; }
export interface RegisterData { email: string; password: string; verification_code?: string; }
export interface SendCodeData { email: string; }
export interface ForgotPasswordData { email: string; }
export interface ResetPasswordData { email: string; code: string; new_password: string; }
export interface TokenResponse { access_token: string; token_type: string; expires_in: number; refresh_token?: string; refresh_expires_in?: number; }
export interface User {
  id: string; email: string; name: string | null; avatar: string | null;
  display_name: string | null; username: string | null;
  subscription_tier: string; subscription_status: string;
  storage_used: number; storage_limit: number; created_at: string;
  is_admin?: boolean;
  // 会员总开关（feature_flags.membership_enabled）：false=全员限时免费，禁售 Pro
  membership_enabled?: boolean;
}

export const TOKEN_KEY = 'access_token';
export const REFRESH_KEY = 'refresh_token';
const AUTH_STORAGE_KEY = 'auth_storage';

// 默认仅本次会话；用户显式选择「7 天内保持登录」后才写 localStorage。
// 刷新令牌时默认沿用原存储位置，避免续期把持久会话降回会话级。
const isPersistentLogin = (): boolean => localStorage.getItem(AUTH_STORAGE_KEY) === 'local';

const readStoredToken = (key: string): string | null => {
  const current = sessionStorage.getItem(key);
  if (current) return current;
  const stored = localStorage.getItem(key);
  if (stored && !isPersistentLogin()) {
    // 兼容旧版本：没有持久登录标记的 localStorage 令牌仍按会话级迁移。
    sessionStorage.setItem(key, stored);
    localStorage.removeItem(key);
  }
  return stored;
};

const writeStoredToken = (key: string, token: string, persistent: boolean): void => {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
  if (persistent) {
    localStorage.setItem(AUTH_STORAGE_KEY, 'local');
    localStorage.setItem(key, token);
  } else {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    sessionStorage.setItem(key, token);
  }
};

const clearStoredToken = (key: string): void => {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
  if (!getToken() && !getRefreshToken()) localStorage.removeItem(AUTH_STORAGE_KEY);
};

export const getToken = (): string | null => readStoredToken(TOKEN_KEY);
export const setToken = (token: string, persistent = isPersistentLogin()): void => {
  writeStoredToken(TOKEN_KEY, token, persistent);
};
export const clearToken = (): void => clearStoredToken(TOKEN_KEY);
export const getRefreshToken = (): string | null => readStoredToken(REFRESH_KEY);
export const setRefreshToken = (token: string, persistent = isPersistentLogin()): void => {
  writeStoredToken(REFRESH_KEY, token, persistent);
};
export const clearRefreshToken = (): void => clearStoredToken(REFRESH_KEY);
export const isAuthenticated = (): boolean => !!getToken();

export const getTokenExpiry = (): number | null => {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    // Base64URL → Base64（处理 -/_ 与 padding）
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) {
      base64 += new Array(5 - pad).join('=');
    }
    const decoded = JSON.parse(atob(base64));
    return decoded.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const isTokenExpiringSoon = (thresholdMinutes = 30): boolean => {
  const exp = getTokenExpiry();
  if (!exp) return false;
  return exp - Date.now() < thresholdMinutes * 60 * 1000;
};

export const authApi = {
  login: (data: LoginData) => api.post<TokenResponse>('/api/v1/auth/login', data),
  register: (data: RegisterData) => api.post<TokenResponse>('/api/v1/auth/register', data),
  sendVerificationCode: (data: SendCodeData) => api.post<{ email: string; message: string }>('/api/v1/auth/send-verification-code', data),
  forgotPassword: (data: ForgotPasswordData) => api.post<{ email: string; message: string }>('/api/v1/auth/forgot-password', data),
  resetPassword: (data: ResetPasswordData) => api.post<{ success: boolean; message: string }>('/api/v1/auth/reset-password', data),
  refresh: () => api.post<TokenResponse>('/api/v1/auth/refresh', null, {
    headers: { Authorization: `Bearer ${getRefreshToken() || getToken()}` },
  }),
  logout: () => api.post('/api/v1/auth/logout'),
  me: () => api.get<User>('/api/v1/users/me'),
};
